import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import {SaveOutlined} from '@material-ui/icons';
import {Controller, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import ContainedButton from '../../../shared/buttons/ContainedButton.tsx';
import TextButton from '../../../shared/buttons/TextButton.tsx';
import LabelValue from '../../../shared/labelvalue/LabelValue.js';

const TAMANHO_MAXIMO_DESCRICAO = 1000;

const FormSchema = Yup.object().shape({
    idDocumento: Yup.number().typeError('É obrigatório').required('É obrigatório'),
    idComposicaoFamiliar: Yup.mixed().nullable(),
    descricao: Yup.string().trim()
        .required('É obrigatória')
        .max(TAMANHO_MAXIMO_DESCRICAO, `Máximo de ${TAMANHO_MAXIMO_DESCRICAO} caracteres`)
});

const VALORES_VAZIOS = {idDocumento: '', idComposicaoFamiliar: '', descricao: ''};

const useStyles = makeStyles(() => ({
    paper: {
        width: 500,
        maxWidth: '100%'
    },
    contentRoot: {
        overflowY: 'initial'
    }
}));

/*
 * Cria ou edita uma pendência de documento da lista em memória da tela.
 * `pendencia` (linha da lista) vem preenchida na edição e nula na criação;
 * `tipos` = documentos do processo seletivo; `membros` = composição familiar.
 * Ao salvar devolve {idDocumento, idComposicaoFamiliar (número ou null), descricao}.
 */
export default function DialogPendenciaDocumento({open, onClose, pendencia, tipos, membros, onSalvar}) {
    const classes = useStyles();
    const {register, control, handleSubmit, reset, formState: {errors, isSubmitting}} = useForm({
        resolver: yupResolver(FormSchema),
        defaultValues: VALORES_VAZIOS
    });

    const preencher = () => reset(pendencia
        ? {
            // Pendência antiga sem tipo: nenhum tipo pré-selecionado e o Salvar exige escolher.
            idDocumento: pendencia.idDocumento === null || pendencia.idDocumento === undefined ? '' : pendencia.idDocumento,
            idComposicaoFamiliar: pendencia.idComposicaoFamiliar === null || pendencia.idComposicaoFamiliar === undefined
                ? ''
                : pendencia.idComposicaoFamiliar,
            descricao: pendencia.descricao || ''
        }
        : VALORES_VAZIOS);

    useEffect(() => {
        if (open) {
            preencher();
        }
    }, [open, pendencia]);

    const handleClose = () => {
        onClose();
        reset(VALORES_VAZIOS);
    };

    const submit = values => {
        onSalvar({
            idDocumento: values.idDocumento,
            idComposicaoFamiliar: values.idComposicaoFamiliar === '' ? null : values.idComposicaoFamiliar,
            descricao: values.descricao.trim()
        });
        handleClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            TransitionProps={{onEntering: preencher}}
            scroll='body'
            aria-labelledby='dialog-pendencia-documento-titulo'
            classes={{paper: classes.paper}}>
            <DialogTitle id='dialog-pendencia-documento-titulo'>
                {pendencia ? 'Editar pendência de documento' : 'Adicionar pendência de documento'}
            </DialogTitle>
            <form noValidate autoComplete='off' onSubmit={handleSubmit(submit)}>
                <DialogContent classes={{root: classes.contentRoot}}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <LabelValue label='Tipo de documento *'>
                                <FormControl variant='outlined' fullWidth error={!!errors.idDocumento}>
                                    <Controller
                                        name='idDocumento'
                                        control={control}
                                        render={({field}) => (
                                            <Select {...field} displayEmpty>
                                                <MenuItem value='' disabled><em>Selecione...</em></MenuItem>
                                                {(tipos || []).map(tipo => (
                                                    <MenuItem key={tipo.id} value={tipo.id}>{tipo.nome}</MenuItem>
                                                ))}
                                            </Select>
                                        )}/>
                                    {!!errors.idDocumento && <FormHelperText error>{errors.idDocumento.message}</FormHelperText>}
                                </FormControl>
                            </LabelValue>
                        </Grid>
                        <Grid item xs={12}>
                            <LabelValue label='Documento para'>
                                <FormControl variant='outlined' fullWidth>
                                    <Controller
                                        name='idComposicaoFamiliar'
                                        control={control}
                                        render={({field}) => (
                                            <Select {...field} displayEmpty>
                                                <MenuItem value=''>Próprio inscrito</MenuItem>
                                                {(membros || []).map(membro => (
                                                    <MenuItem key={membro.id} value={membro.id}>{membro.nome}</MenuItem>
                                                ))}
                                            </Select>
                                        )}/>
                                </FormControl>
                            </LabelValue>
                        </Grid>
                        <Grid item xs={12}>
                            <LabelValue label='Descrição *'>
                                <FormControl variant='outlined' fullWidth>
                                    <OutlinedInput
                                        {...register('descricao')}
                                        multiline
                                        minRows={3}
                                        error={!!errors.descricao}
                                        inputProps={{maxLength: TAMANHO_MAXIMO_DESCRICAO}}/>
                                    {!!errors.descricao && <FormHelperText error>{errors.descricao.message}</FormHelperText>}
                                </FormControl>
                            </LabelValue>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <TextButton onClick={handleClose}>Fechar</TextButton>
                    <ContainedButton type='submit' startIcon={<SaveOutlined/>} disabled={isSubmitting}>
                        Salvar
                    </ContainedButton>
                </DialogActions>
            </form>
        </Dialog>
    );
}

DialogPendenciaDocumento.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    pendencia: PropTypes.object,
    tipos: PropTypes.array,
    membros: PropTypes.array,
    onSalvar: PropTypes.func.isRequired
};
